import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "../../design-system/utils/cn";

export interface PopoverProps {
  /** The visible trigger (typically a button). */
  trigger: ReactNode;
  /** Panel content; receives a `close` callback. */
  children: ReactNode | ((close: () => void) => ReactNode);
  align?: "start" | "end";
  /** Which side of the trigger the panel opens on (top = drop-up, e.g. for
   *  triggers at the bottom of the viewport). */
  side?: "bottom" | "top";
  /** Render the panel in a body portal (fixed to the trigger's viewport
   *  position). Use for triggers inside overflow/stacking containers — e.g.
   *  a scroll-capped list — where an in-place panel would be clipped. The
   *  panel closes on scroll rather than tracking the trigger. */
  portal?: boolean;
  /** Stretch the popover (and its trigger) to the container's width, for
   *  full-width form fields — the default hugs the trigger, so a `w-full`
   *  trigger would otherwise collapse to its content width. Lets the panel use
   *  `w-full` to line up with the field. */
  block?: boolean;
  className?: string;
}

/* A minimal popover: toggles a floating panel and closes on outside-click or
 * Escape. Used for the filter-bar dropdowns and the API-key row menus. */
export function Popover({
  trigger,
  children,
  align = "start",
  side = "bottom",
  portal = false,
  block = false,
  className,
}: PopoverProps) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || panelRef.current?.contains(t)) return;

      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // A portal panel is fixed to the trigger's position at open time; any scroll
  // would leave it floating detached, so just close instead of tracking.
  useEffect(() => {
    if (!open || !portal) return;

    const onScroll = () => setOpen(false);
    document.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, portal]);

  const toggle = () => {
    if (!open && portal && ref.current) {
      setRect(ref.current.getBoundingClientRect());
    }

    setOpen((o) => !o);
  };

  const panelClass = cn(
    "z-40 min-w-[220px] rounded-lg border bg-surface p-1.5 shadow-lg",
    !portal && "absolute",
    !portal && (side === "top" ? "bottom-full mb-2" : "top-full mt-2"),
    !portal && (align === "end" ? "right-0" : "left-0"),
    className,
  );

  const portalStyle: CSSProperties | undefined =
    portal && rect
      ? {
          position: "fixed",
          ...(side === "top"
            ? { bottom: window.innerHeight - rect.top + 8 }
            : { top: rect.bottom + 8 }),
          ...(align === "end"
            ? { right: window.innerWidth - rect.right }
            : { left: rect.left }),
        }
      : undefined;

  const panel = open && (
    <div ref={panelRef} className={panelClass} style={portalStyle}>
      {typeof children === "function"
        ? children(() => setOpen(false))
        : children}
    </div>
  );

  return (
    <div ref={ref} className={cn("relative inline-flex", block && "w-full")}>
      <span onClick={toggle} className={cn("inline-flex", block && "w-full")}>
        {trigger}
      </span>
      {portal ? panel && createPortal(panel, document.body) : panel}
    </div>
  );
}
